import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { FiUsers, FiTrendingUp, FiCheckCircle, FiAward, FiEye } from "react-icons/fi";
import { useCycle } from "../../context/CycleContext";
import { getManagementDashboard } from "../../services/dashboardApi";
import useAsync from "../../hooks/useAsync";
import { Card, PageHeader, StatTile, Loader, ErrorState, ChartFrame } from "../../components/common";
import { CompletionBars, DepartmentScoreChart } from "./HrDashboard";
import { axisProps, tooltipProps } from "../../utils/chartTheme";
import { formatScore } from "../../utils/format";

const api = { getManagementDashboard };

export default function ManagementDashboard() {
  const { cycleId, cycle } = useCycle();
  const { data, loading, error, reload } = useAsync(() => api.getManagementDashboard(cycleId), [cycleId], { skip: !cycleId });

  if (loading || !data) return error ? <ErrorState message={error} onRetry={reload} /> : <Loader />;

  const prev = data.previousCycle;
  const delta = prev?.avgScore != null && data.avgScore != null ? Math.round((data.avgScore - prev.avgScore) * 10) / 10 : null;
  const compare = data.ratingDistribution.map((r) => ({
    rating: r.rating.split(" ")[0],
    [cycle?.name || "Current"]: r.count,
    ...(prev ? { [prev.name]: prev.ratingDistribution.find((p) => p.rating === r.rating)?.count || 0 } : {}),
  }));
  const rated = data.ratingDistribution.reduce((s, r) => s + r.count, 0);

  return (
    <div className="page">
      <PageHeader
        title="Leadership summary"
        subtitle={`Aggregated performance for ${cycle?.name}. Individual comments are not shown here.`}
        actions={<span className="readonly-chip"><FiEye size={14} aria-hidden="true" /> Read-only</span>}
      />

      <div className="stat-grid">
        <StatTile icon={FiUsers} label="Active employees" value={data.headcount} />
        <StatTile icon={FiTrendingUp} tone="info" label="Average score" value={formatScore(data.avgScore)} hint={delta === null ? "No comparison yet" : `${delta >= 0 ? "+" : ""}${delta} vs ${prev.name}`} />
        <StatTile icon={FiAward} tone="warning" label="Employees rated" value={rated} hint={`of ${data.headcount}`} />
        <StatTile icon={FiCheckCircle} tone="success" label="Appraisals finalized" value={`${data.completion.finalized}%`} />
      </div>

      <div className="grid-2">
        <Card title="Rating distribution" subtitle={prev ? `Compared with ${prev.name}` : undefined}>
          <ChartFrame
            label="Rating distribution"
            summary={compare.map((d) => ({ label: d.rating, value: Object.entries(d).filter(([k]) => k !== "rating").map(([k, v]) => `${k} ${v}`).join(", ") }))}
          >
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={compare} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#EEF0F6" />
                <XAxis dataKey="rating" {...axisProps} />
                <YAxis allowDecimals={false} {...axisProps} />
                <Tooltip {...tooltipProps} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey={cycle?.name || "Current"} fill="#5B4FE0" radius={[6, 6, 0, 0]} maxBarSize={32} />
                {prev && <Bar dataKey={prev.name} fill="#C9C4F6" radius={[6, 6, 0, 0]} maxBarSize={32} />}
              </BarChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Card>
        <Card title="Cycle completion">
          <CompletionBars completion={data.completion} />
        </Card>
      </div>

      <div className="grid-2">
        <Card title="Average score by department" subtitle={cycle?.name}>
          <div className="chart-box"><DepartmentScoreChart data={data.departments} /></div>
        </Card>
        {prev && (
          <Card title="Average score by department" subtitle={prev.name}>
            <div className="chart-box"><DepartmentScoreChart data={prev.departments} /></div>
          </Card>
        )}
      </div>
    </div>
  );
}
